import moment from "moment";

/**
 * Format class for formatting various data types.
 */
export default class Format {
  /**
   * Format a date value.
   * @param {*} date - The value to format as a date (any value accepted by
   * moment).
   * @returns {string} The formatted date string (M/D/YYYY) or empty string if
   * invalid.
   */
  static date(date) {
    let m;
    if (!date || !(m = moment.utc(date)).isValid()) {
      return "";
    }
    return m.local().format("M/D/YYYY");
  }

  /**
   * Format a date value that is already in local time (no UTC conversion).
   * @param {*} date - The value to format as a date (any value accepted by
   * moment).
   * @returns {string} The formatted date string (M/D/YYYY) or empty string if
   * invalid.
   */
  static localDate(date) {
    let m;
    if (!date || !(m = moment(date)).isValid()) {
      return "";
    }
    return m.format("M/D/YYYY");
  }

  /**
   * Format a date/time value in UTC.
   * @param {*} date - The value to format as a date/time (any value accepted
   * by moment).
   * @returns {string} The formatted date/time string (M/D/YYYY h:mma) or
   * empty string if invalid.
   */
  static dateTime(date) {
    let m;
    if (!date || !(m = moment.utc(date)).isValid()) {
      return "";
    }
    return m.local().format("M/D/YYYY h:mma");
  }

  /**
   * Format an address.
   * @param {string} address - The address to format.
   * @param {string} city - The city to format.
   * @param {string} state - The state to format.
   * @param {string} postal_code - The postal_code to format.
   * @returns {JSX.Element} The formatted address.
   */
  static address(address, city, state, postal_code) {
    const line1 = address ? address : "";
    let line2 = "";
    if (city) {
      line2 += city;
    }
    if (state) {
      line2 += (line2 ? ", " : "") + state;
    }
    if (postal_code) {
      line2 += (line2 ? " " : "") + postal_code;
    }
    if (!line1 && !line2) {
      return "";
    }
    if (!line1) {
      return line2;
    }
    if (!line2) {
      return line1;
    }
    return (
      <>
        {line1}
        <br />
        {line2}
      </>
    );
  }

  /**
   * Format a phone number.
   * @param {string} phone - The phone number to format.
   * @param {boolean} preserveData - Defaults to false.
   * Mode 1 (`preserveData = false`): extraction mode. Attempts to extract a
   * phone number from noisy input by removing non-digits and a leading NANP
   * country code, then formats the first 10 digits when available. Returns
   * the original value when fewer than 10 digits are present.
   * Mode 2 (`preserveData = true`): data-preserving mode. Only values
   * containing digits, spaces, dashes, and parentheses are considered.
   * Formats only when exactly 10 digits are present; otherwise returns the
   * original value to avoid losing information.
   * @returns {string} A formatted phone number or the original input.
   */
  static phone(phone, preserveData = false) {
    if (!phone) {
      return "";
    }

    let digits = phone.replace(/\D/g, "");
    const format = (tenDigits) => {
      const area = tenDigits.slice(0, 3);
      const prefix = tenDigits.slice(3, 6);
      const line = tenDigits.slice(6, 10);
      return `(${area}) ${prefix}-${line}`;
    };

    if (preserveData) {
      // If anything except digits, spaces, dashes, and parentheses,
      // return unformatted string. Also if not 10 digits return
      // unformatted string.
      if (/[^0-9\s\-()]/.test(phone) || digits.length !== 10) {
        return phone;
      } else {
        return format(digits);
      }
    } else {
      digits = digits.replace(/^1(?=\d{10}$)/, "");

      // As long as there's at least 10 digits, use those to format.
      if (digits.length >= 10) {
        return format(digits.slice(0, 10));
      } else {
        return phone;
      }
    }
  }

  /**
   * Formats a file size in bytes for display.
   * @param {number|string} byteCount The file size in bytes.
   * @returns {string} The formatted file size.
   */
  static fileSize(byteCount) {
    const bytes = Number(byteCount);

    if (!bytes) {
      return "";
    }

    if (bytes < 1024) {
      return `${bytes}B`;
    }

    if (bytes < 1024 * 1024) {
      return `${(bytes / 1024).toFixed(1)}KB`;
    }

    return `${(bytes / (1024 * 1024)).toFixed(1)}MB`;
  }

  /**
   * Format a string to title case.
   * @param {string} string - The string to format.
   * @returns {string} The formatted string.
   */
  static titleCase(string) {
    return string
      ? string
          .split("-")
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
          .join(" ")
      : "";
  }

  /**
   * Formats a numeric value for display, or optionally returns the rounded numeric value
   * for use in calculations.
   *
   * @param {number|string|null|undefined} number Value to format.
   * @param {object} [options] Optional settings.
   * @param {number} [options.decimalPlaceCount] Number of decimal places to display.
   * @param {boolean} [options.shouldRoundValueToDecimalPlacesBeforeFormatting] When true, rounds the value before formatting.
   * @param {boolean} [options.shouldReturnRoundedNumericValue] When true, returns a number instead of a formatted string.
   * @returns {string|number} A formatted string by default, or a rounded number when shouldReturnRoundedNumericValue is true.
   */
  static number(number, options = {}) {
    if (number === null || number === undefined) {
      return options.shouldReturnRoundedNumericValue ? 0 : "";
    }

    const parsedNumber = Number(number);
    if (!Number.isFinite(parsedNumber)) {
      return options.shouldReturnRoundedNumericValue ? 0 : "";
    }

    const hasDecimalPlaceCount =
      Number.isFinite(options.decimalPlaceCount) &&
      options.decimalPlaceCount >= 0;

    const safeDecimalPlaceCount = hasDecimalPlaceCount
      ? Math.min(20, Math.floor(options.decimalPlaceCount))
      : 0;

    let valueAfterOptionalRounding = parsedNumber;
    if (
      options.shouldRoundValueToDecimalPlacesBeforeFormatting &&
      hasDecimalPlaceCount
    ) {
      const scale = 10 ** safeDecimalPlaceCount;
      valueAfterOptionalRounding = Math.round(parsedNumber * scale) / scale;
    } else if (!hasDecimalPlaceCount) {
      valueAfterOptionalRounding = Math.round(parsedNumber);
    }

    if (options.shouldReturnRoundedNumericValue) {
      return valueAfterOptionalRounding;
    }

    return new Intl.NumberFormat("en-US", {
      minimumFractionDigits: safeDecimalPlaceCount,
      maximumFractionDigits: safeDecimalPlaceCount,
      useGrouping: true,
    }).format(valueAfterOptionalRounding);
  }

  /**
   * Formats a numeric value as a percentage string for display.
   * Values strictly between 0 and 1 are treated as fractions (for example 0.7525 becomes "75.25%").
   * All other finite numbers are formatted as the percentage magnitude itself (for example 75 becomes "75%").
   *
   * @param {number|string|null|undefined} ratioOrPercentage Value from the server or user interface.
   * @returns {string|null} A string ending with "%", or null when the value is missing or not a finite number.
   */
  static percentage(ratioOrPercentage) {
    if (!ratioOrPercentage) {
      return null;
    }
    const numericValue = Number(ratioOrPercentage);

    const percentageMagnitude =
      numericValue > 0 && numericValue < 1 ? numericValue * 100 : numericValue;
    const formattedPercentageBody = new Intl.NumberFormat("en-US", {
      minimumFractionDigits: Number.isInteger(percentageMagnitude) ? 0 : 2,
      maximumFractionDigits: 2,
    }).format(percentageMagnitude);

    return `${formattedPercentageBody}%`;
  }

  /**
   * Formats a numeric value as USD currency for display.
   * @param {number|string|null|undefined} amount
   * @returns {string}
   */
  static currency(amount) {
    if (amount == null || amount === "") {
      return "";
    }

    const parsedAmount = Number(amount);

    if (!Number.isFinite(parsedAmount)) {
      return "";
    }

    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(parsedAmount);
  }
}
